/* eslint react/no-unused-prop-types: 0 */
// Allow props to be used only in getRouterOrOutletProps without being destructured
import type { FC } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRequest } from 'loti-request';

import createServerErrorNotification from 'app/components/ServerErrorNotification';
import ConfirmModal from 'app/components/Modal/ConfirmModal';
import { getApiUrl } from 'app/utils/api';
import { getRouterOrOutletProps } from 'app/utils/routing';
import type { Blueprint } from 'app/pages/BlueprintsPage/types';

interface Props {
  blueprints?: Blueprint[];
  closeModal?: () => void;
}

const DeleteBlueprintModal: FC<Props> = props => {
  const [t] = useTranslation();
  const routeParams = useParams();
  const outletProps = useOutletContext<Props>();

  const { blueprints, closeModal } = getRouterOrOutletProps(props, outletProps);

  const blueprint = blueprints.find(({ id }) => id.toString() === routeParams.id);
  const url = blueprint ? `/blueprints/${blueprint.id}` : '';
  const { withLoader: loading, fetch: deleteBlueprint } = useRequest({
    url: getApiUrl(url),
    method: 'DELETE',
    onError: (e, _, params) => createServerErrorNotification(e, params),
    loaderDelay: 500,
    onSuccess: () => {
      closeModal();
    }
  });

  return (
    <>
      {blueprint && (
        <ConfirmModal
          displayed
          title={t('blueprints-delete-title')}
          variation="error"
          onCancel={closeModal}
          onConfirm={() => deleteBlueprint()}
          cancelText={t('common-cancel')}
          confirmText={t('blueprints-delete-confirm')}
          loading={loading}
        >
          <>
            {t('blueprints-delete-sure_to_delete')} <span>&apos;{blueprint.name}&apos;</span>?
          </>
        </ConfirmModal>
      )}
    </>
  );
};

DeleteBlueprintModal.displayName = 'DeleteBlueprintModal';

export default DeleteBlueprintModal;
